// src/composable/gameMatches.js
import { ref, onMounted, onUnmounted } from "vue";

/**
 * useMatch3Game(gameContainerRef, options)
 *
 * options:
 *   - cols (default 7) - количество колонок
 *   - rows (default 9) - количество рядов
 *   - tileSize (default 80) - размер тайла
 *   - gap (default 1) - отступ между тайлами
 *   - tileKeys: array of asset keys (["tile1","tile2",...])
 *   - onEndGame(finalScore)
 *
 * returns:
 *   { score, newGame, showHint, endGame, pause, resume, resize }
 */
export function useMatch3Game(gameContainerRef, options = {}) {
	const score = ref(0);

	const cols = options.cols || 7;
	const rows = options.rows || 9;
	const tileSize = options.tileSize || 80;
	const gap = options.gap ?? 1;

	const cfg = {
		cols,
		rows,
		tileSize,
		gap,
		tileKeys: options.tileKeys || ["tile_green_1", "tile_green_2", "tile_green_3", "tile_green_4"],
		// tileKeys: options.tileKeys || ["tile1", "tile2", "tile3", "tile4"],
		width: cols * tileSize + (cols - 1) * gap,
		height: rows * tileSize + (rows - 1) * gap,
		onEndGame: typeof options.onEndGame === "function" ? options.onEndGame : () => {},
		onGameReady: typeof options.onGameReady === "function" ? options.onGameReady : () => {},
	};

	let phaserGame = null;
	let sceneInstance = null;

	onMounted(async () => {
		if (typeof window === "undefined") return;

		// Dynamic import Phaser only on client
		const Phaser = (await import("phaser")).default;

		// Hooks for scene -> vue communication
		window.__MATCH3_HOOKS__ = {
			setScore: (v) => (score.value = v),
			onEndGame: cfg.onEndGame,
			onGameReady: cfg.onGameReady,
		};

		const phConfig = {
			type: Phaser.CANVAS,
			parent: gameContainerRef.value,
			width: cfg.width,
			height: cfg.height,
			scene: Match3SceneFactory(cfg, Phaser),
			transparent: true,
			scale: {},
			input: {
				activePointers: 1,
				touch: {
					capture: true
				}
			}
		};

		phaserGame = new Phaser.Game(phConfig);
	});

	onUnmounted(() => {
		if (phaserGame) {
			phaserGame.destroy(true);
			phaserGame = null;
			sceneInstance = null;
		}
	});

	function newGame() {
		if (!phaserGame) return;
		const sc = phaserGame.scene.keys["Match3Scene"];
		if (sc) sc.resetGame();
	}

	function showHint() {
		const sc = phaserGame && phaserGame.scene.keys["Match3Scene"];
		if (sc) sc.flashHint();
	}

	function endGame() {
		const sc = phaserGame && phaserGame.scene.keys["Match3Scene"];
		if (sc) sc.finishGame();
	}

	function pause() {
		const sc = phaserGame && phaserGame.scene.keys["Match3Scene"];
		if (sc) sc.scene.pause();
	}
	function resume() {
		const sc = phaserGame && phaserGame.scene.keys["Match3Scene"];
		if (sc) sc.scene.resume();
	}

	function resize(newTileSize) {
		if (!phaserGame) return;
		const sc = phaserGame.scene.keys["Match3Scene"];
		if (sc) {
			sc.resizeGame(newTileSize);
		}
	}

	return {
		score,
		newGame,
		showHint,
		endGame,
		pause,
		resume,
		resize,
	};
}

/* -----------------------------
   Scene factory — чтобы скрыть cfg
   ----------------------------- */
function Match3SceneFactory(cfg, Phaser) {
	return class Match3Scene extends Phaser.Scene {
		constructor() {
			super({ key: "Match3Scene" });
			this.cfg = cfg;
			this.Phaser = Phaser;

			// game state
			this.grid = []; // 2D array of tile objects or null
			this.tilesGroup = null;
			this.selected = null;
			this.isResolving = false;
			this.score = 0;
			this.validMoves = [];
			this.hintTweenIds = [];
		}

		init() {
			// Let the composable find this instance if needed
			window.__MATCH3_SCENE__ = this;
		}

		preload() {
			// load tiles: user should place files in /public/assets/tiles/<key>.png
			this.cfg.tileKeys.forEach((k) => {
				// If assets are located elsewhere, change the path accordingly.
				this.load.image(k, `/img/tiles/${k}.png`);
			});
		}

		create() {
			// group for tiles (not required but useful)
			this.tilesGroup = this.add.group();

			// create logical board and sprites
			this.tileW = this.cfg.tileSize;
			this.tileH = this.cfg.tileSize;
			this.gap = this.cfg.gap;
			this.cols = this.cfg.cols;
			this.rows = this.cfg.rows;
			this.offsetX = 0;
			this.offsetY = 0;

			// center board in canvas (с учётом gap)
			const totalW = this.cols * this.tileW + (this.cols - 1) * this.gap;
			const totalH = this.rows * this.tileH + (this.rows - 1) * this.gap;
			this.offsetX = Math.floor((this.cameras.main.width - totalW) / 2);
			this.offsetY = Math.floor((this.cameras.main.height - totalH) / 2);
			// this.offsetX = 0;                                                                             
  			// this.offsetY = 0;  

			// Прозрачный фон - линии будут цветом контейнера
			// this.cameras.main.setBackgroundColor('transparent');
			// this.cameras.main.setBackgroundColor(0x0d2e1a);
			this.cameras.main.setBackgroundColor(0xffd08e);

			this.makeEmptyGrid();
			this.fillInitialBoard();

			// Input handling already attached to sprites when created

			// compute valid moves initially
			this.updateValidMoves();

			// initialize score (push to Vue)
			this.score = 0;
			window.__MATCH3_HOOKS__.setScore(this.score);
			this.scale.updateBounds();

			// Notify that game is ready
			window.__MATCH3_HOOKS__.onGameReady();
		}

		/* ---------------------------
       Grid and tiles helpers
       --------------------------- */
		makeEmptyGrid() {
			this.grid = new Array(this.rows);
			for (let y = 0; y < this.rows; y++) {
				this.grid[y] = new Array(this.cols).fill(null);
			}
		}

		// Spawn sprite at board coordinates (x: col, y: row)
		spawnSprite(x, y, key, yOffset = 0) {
			const px = this.offsetX + x * (this.tileW + this.gap);
			const py = this.offsetY + y * (this.tileH + this.gap) + yOffset;
			const img = this.add.image(px, py, key).setOrigin(0);

			// Сохраняем оригинальный размер текстуры ДО масштабирования
			const originalWidth = img.frame.width;
			const originalHeight = img.frame.height;

			// Устанавливаем размер через displayWidth/Height
			img.displayWidth = this.tileW;
			img.displayHeight = this.tileH;

			// Hit area должна быть в координатах ОРИГИНАЛЬНОГО размера текстуры
			// Phaser автоматически масштабирует hit area вместе со спрайтом
			img.setInteractive(
				new this.Phaser.Geom.Rectangle(0, 0, originalWidth, originalHeight),
				this.Phaser.Geom.Rectangle.Contains
			);

			// logical props
			img.boardX = x;
			img.boardY = y;
			img.tileKey = key;

			// events
			img.on("pointerdown", () => {
				this.onTilePointerDown(img);
			});

			this.tilesGroup.add(img);
			return img;
		}

		fillInitialBoard() {
			// Fill board with random tiles, avoid initial matches
			for (let y = 0; y < this.rows; y++) {
				for (let x = 0; x < this.cols; x++) {
					let k;
					do {
						k = this.Phaser.Utils.Array.GetRandom(this.cfg.tileKeys);
					} while (this.causesInitialMatch(x, y, k));
					const spr = this.spawnSprite(x, y, k, -this.tileH * 2);
					this.grid[y][x] = spr;
					// drop animation for initial drop
					this.tweenToPosition(spr, this.getWorldPosY(y));
				}
			}
		}

		causesInitialMatch(x, y, key) {
			// check left-left and left or up-up and up
			// horizontal
			if (x >= 2) {
				const a = this.grid[y][x - 1];
				const b = this.grid[y][x - 2];
				if (a && b && a.tileKey === key && b.tileKey === key) return true;
			}
			// vertical
			if (y >= 2) {
				const a = this.grid[y - 1][x];
				const b = this.grid[y - 2][x];
				if (a && b && a.tileKey === key && b.tileKey === key) return true;
			}
			return false;
		}

		getWorldPosX(x) {
			return this.offsetX + x * (this.tileW + this.gap);
		}
		getWorldPosY(y) {
			return this.offsetY + y * (this.tileH + this.gap);
		}

		/* ---------------------------
       Input & swap handling
       --------------------------- */
		onTilePointerDown(tile) {
			if (this.isResolving) return;
			if (!this.selected) {
				this.selectTile(tile);
				return;
			}
			if (this.selected === tile) {
				this.deselectTile();
				return;
			}

			// if neighbor -> attempt swap
			if (this.areNeighbors(this.selected, tile)) {
				this.attemptSwap(this.selected, tile);
			} else {
				this.deselectTile();
				this.selectTile(tile);
			}
		}

		selectTile(tile) {
			this.selected = tile;
			tile.setTint(0x999999);
		}
		deselectTile() {
			if (this.selected) this.selected.clearTint();
			this.selected = null;
		}

		areNeighbors(a, b) {
			const dx = Math.abs(a.boardX - b.boardX);
			const dy = Math.abs(a.boardY - b.boardY);
			return dx + dy === 1;
		}

		async attemptSwap(a, b) {
			if (this.isResolving) return;
			this.isResolving = true;

			// animate swap
			await this.swapWithAnimation(a, b);

			// swap in grid
			this.swapInGrid(a, b);

			// check matches
			const groups = this.findAllMatches();
			if (groups.length === 0) {
				// revert swap
				await this.swapWithAnimation(a, b);
				this.swapInGrid(a, b);
				this.isResolving = false;
				this.deselectTile();
				return;
			}

			// valid move
			await this.resolveCascades(groups);
			this.isResolving = false;
			this.deselectTile();
			this.updateValidMoves();
		}

		swapInGrid(a, b) {
			// swap board positions (grid) and update boardX/boardY
			const ax = a.boardX,
				ay = a.boardY,
				bx = b.boardX,
				by = b.boardY;
			this.grid[ay][ax] = b;
			this.grid[by][bx] = a;

			a.boardX = bx;
			a.boardY = by;
			b.boardX = ax;
			b.boardY = ay;
		}

		swapWithAnimation(a, b) {
			return new Promise((resolve) => {
				const ax = this.getWorldPosX(a.boardX),
					ay = this.getWorldPosY(a.boardY);
				const bx = this.getWorldPosX(b.boardX),
					by = this.getWorldPosY(b.boardY);

				this.tweens.add({
					targets: a,
					x: bx,
					y: by,
					duration: 160,
					ease: "Power2",
				});
				this.tweens.add({
					targets: b,
					x: ax,
					y: ay,
					duration: 160,
					ease: "Power2",
					onComplete: () => {
						resolve();
					},
				});
			});
		}

		tweenToPosition(sprite, targetY, duration = 220) {
			return new Promise((resolve) => {
				this.tweens.add({
					targets: sprite,
					y: targetY,
					duration,
					ease: "Cubic",
					onComplete: () => resolve(),
				});
			});
		}

		fadeOutAndDestroy(sprite) {
			return new Promise((resolve) => {
				this.tweens.add({
					targets: sprite,
					alpha: 0,
					scaleX: 0.95,
					scaleY: 0.95,
					duration: 180,
					onComplete: () => {
						sprite.destroy();
						resolve();
					},
				});
			});
		}

		/* ---------------------------
       Match detection
       returns array of Sets of cell ids (y*cols + x)
       --------------------------- */
		findAllMatches() {
			const segments = [];

			// horizontal
			for (let y = 0; y < this.rows; y++) {
				let runKey = null;
				let runStart = 0;
				for (let x = 0; x <= this.cols; x++) {
					const key = x < this.cols && this.grid[y][x] ? this.grid[y][x].tileKey : null;
					if (key === runKey) {
						// continue
					} else {
						const len = x - runStart;
						if (runKey !== null && len >= 3) {
							const s = new Set();
							for (let cx = runStart; cx < x; cx++) s.add(y * this.cols + cx);
							segments.push(s);
						}
						runKey = key;
						runStart = x;
					}
				}
			}

			// vertical
			for (let x = 0; x < this.cols; x++) {
				let runKey = null;
				let runStart = 0;
				for (let y = 0; y <= this.rows; y++) {
					const key = y < this.rows && this.grid[y][x] ? this.grid[y][x].tileKey : null;
					if (key === runKey) {
						// continue
					} else {
						const len = y - runStart;
						if (runKey !== null && len >= 3) {
							const s = new Set();
							for (let cy = runStart; cy < y; cy++) s.add(cy * this.cols + x);
							segments.push(s);
						}
						runKey = key;
						runStart = y;
					}
				}
			}

			if (segments.length === 0) return [];

			// merge overlapping segments
			const groups = [];
			segments.forEach((seg) => {
				let mergedInto = -1;
				for (let gi = 0; gi < groups.length; gi++) {
					if (overlaps(groups[gi], seg)) {
						seg.forEach((v) => groups[gi].add(v));
						mergedInto = gi;
						break;
					}
				}
				if (mergedInto === -1) {
					groups.push(new Set(seg));
				} else {
					// merge any other now-overlapping groups
					let changed = true;
					while (changed) {
						changed = false;
						for (let gi = groups.length - 1; gi >= 0; gi--) {
							if (gi === mergedInto) continue;
							if (overlaps(groups[mergedInto], groups[gi])) {
								groups[gi].forEach((id) => groups[mergedInto].add(id));
								groups.splice(gi, 1);
								changed = true;
							}
						}
					}
				}
			});

			function overlaps(a, b) {
				for (const v of a) if (b.has(v)) return true;
				return false;
			}

			return groups;
		}

		/* ---------------------------
       Cascades: remove matches, collapse, refill
       --------------------------- */
		async resolveCascades(initialGroups) {
			let groups = initialGroups || this.findAllMatches();
			if (!groups || groups.length === 0) return;

			// collect all matched positions
			const allMatched = new Set();
			groups.forEach((g) => g.forEach((id) => allMatched.add(id)));

			// animate fade-out and destroy matched sprites
			const spritesToRemove = [];
			allMatched.forEach((id) => {
				const x = id % this.cols;
				const y = Math.floor(id / this.cols);
				const spr = this.grid[y][x];
				if (spr) spritesToRemove.push(spr);
				this.grid[y][x] = null; // mark as cleared
			});

			// update score: 5 points per tile
			groups.forEach((g) => {
				this.score += g.size * 5;
			});
			window.__MATCH3_HOOKS__.setScore(this.score);

			// fade out matched sprites in parallel
			await Promise.all(spritesToRemove.map((s) => this.fadeOutAndDestroy(s)));

			// collapse columns and animate falling
			await this.collapseColumns();

			// refill top with new tiles
			await this.refillBoard();

			// after refill, look for new matches (chain)
			const next = this.findAllMatches();
			if (next.length > 0) {
				// small delay to show refill
				await this.delay(80);
				await this.resolveCascades(next);
			} else {
				// finished resolving cascade: update valid moves
				this.updateValidMoves();
			}
		}

		collapseColumns() {
			// For every column, move tiles down to fill nulls
			const promises = [];
			for (let x = 0; x < this.cols; x++) {
				let write = this.rows - 1;
				for (let y = this.rows - 1; y >= 0; y--) {
					const spr = this.grid[y][x];
					if (spr !== null) {
						if (y !== write) {
							// move sprite to new board pos
							this.grid[write][x] = spr;
							spr.boardY = write;
							this.grid[y][x] = null;
							// animate fall for spr to new Y
							const targetY = this.getWorldPosY(write);
							promises.push(this.tweenToPosition(spr, targetY, 200 + (this.rows - write) * 8));
						}
						write--;
					}
				}
			}
			return Promise.all(promises);
		}

		refillBoard() {
			const promises = [];
			for (let x = 0; x < this.cols; x++) {
				let write = this.rows - 1;
				for (let y = this.rows - 1; y >= 0; y--) {
					if (this.grid[y][x] === null) {
						// create new tile above the board
						const key = this.Phaser.Utils.Array.GetRandom(this.cfg.tileKeys);
						const spr = this.spawnSprite(x, -1, key, -this.tileH * 2); // start offscreen
						// place logically at (y,x)
						this.grid[y][x] = spr;
						spr.boardX = x;
						spr.boardY = y;
						spr.x = this.getWorldPosX(x);
						spr.y = this.getWorldPosY(-1); // starting above
						// tween to correct y
						promises.push(this.tweenToPosition(spr, this.getWorldPosY(y), 260 + y * 10));
					}
				}
			}
			return Promise.all(promises);
		}

		delay(ms) {
			return new Promise((res) => setTimeout(res, ms));
		}

		/* ---------------------------
       Valid moves / hint
       --------------------------- */
		updateValidMoves() {
			this.validMoves = this.findValidMoves();
			// For debugging you can log validMoves
			// console.log("validMoves", this.validMoves.length);
		}

		// returns array of pairs [{a:{x,y}, b:{x,y}}]
		findValidMoves() {
			const moves = [];
			for (let y = 0; y < this.rows; y++) {
				for (let x = 0; x < this.cols; x++) {
					// right
					if (x + 1 < this.cols && this.grid[y][x].tileKey !== this.grid[y][x + 1].tileKey) {
						if (this.wouldSwapCreateMatch(x, y, x + 1, y)) moves.push({ a: { x, y }, b: { x: x + 1, y } });
					}
					// down
					if (y + 1 < this.rows && this.grid[y][x].tileKey !== this.grid[y + 1][x].tileKey) {
						if (this.wouldSwapCreateMatch(x, y, x, y + 1)) moves.push({ a: { x, y }, b: { x, y: y + 1 } });
					}
				}
			}
			return moves;
		}

		wouldSwapCreateMatch(ax, ay, bx, by) {
			// swap types in temp
			const a = this.grid[ay][ax];
			const b = this.grid[by][bx];
			const ta = a.tileKey;
			const tb = b.tileKey;
			// temp swap
			a.tileKey = tb;
			b.tileKey = ta;
			const ok = this.testAt(ax, ay) || this.testAt(bx, by);
			// revert
			a.tileKey = ta;
			b.tileKey = tb;
			return ok;
		}

		testAt(x, y) {
			const t = this.grid[y][x].tileKey;
			// horizontal
			let lenH = 1;
			for (let cx = x - 1; cx >= 0 && this.grid[y][cx] && this.grid[y][cx].tileKey === t; cx--) lenH++;
			for (let cx = x + 1; cx < this.cols && this.grid[y][cx] && this.grid[y][cx].tileKey === t; cx++) lenH++;
			if (lenH >= 3) return true;
			// vertical
			let lenV = 1;
			for (let cy = y - 1; cy >= 0 && this.grid[cy][x] && this.grid[cy][x].tileKey === t; cy--) lenV++;
			for (let cy = y + 1; cy < this.rows && this.grid[cy][x] && this.grid[cy][x].tileKey === t; cy++) lenV++;
			return lenV >= 3;
		}

		// animate hint: pulse two tiles and remove pulse
		flashHint(duration = 1000) {
			if (!this.validMoves || this.validMoves.length === 0) {
				// no valid moves -> shuffle board
				this.shuffleBoard();
				return;
			}
			const pick = this.Phaser.Utils.Array.GetRandom(this.validMoves);
			const a = this.grid[pick.a.y][pick.a.x];
			const b = this.grid[pick.b.y][pick.b.x];

			const tintColor = 0xffff66;
			a.setTint(tintColor);
			b.setTint(tintColor);

			setTimeout(() => {
				if (a) a.clearTint();
				if (b) b.clearTint();
			}, duration);
		}

		/* ---------------------------
       Utility: shuffleBoard
       --------------------------- */
		shuffleBoard() {
			// Flatten keys, shuffle, assign
			const flat = [];
			for (let y = 0; y < this.rows; y++) for (let x = 0; x < this.cols; x++) flat.push(this.grid[y][x].tileKey);
			this.Phaser.Utils.Array.Shuffle(flat);
			for (let y = 0, k = 0; y < this.rows; y++) {
				for (let x = 0; x < this.cols; x++) {
					const key = flat[k++];
					const spr = this.grid[y][x];
					spr.tileKey = key;
					spr.setTexture(key);
				}
			}
			// after shuffle we should ensure no immediate matches; if matches exist, shuffle again
			const groups = this.findAllMatches();
			if (groups.length > 0) this.shuffleBoard();
			else this.updateValidMoves();
		}

		/* ---------------------------
       External API methods
       --------------------------- */
		resetGame() {
			// clear all sprites
			this.tilesGroup.clear(true, true);
			this.makeEmptyGrid();
			this.fillInitialBoard();
			this.score = 0;
			window.__MATCH3_HOOKS__.setScore(this.score);
			this.updateValidMoves();
		}

		finishGame() {
			// trigger callback
			window.__MATCH3_HOOKS__.onEndGame(this.score);
		}

		resizeGame(newTileSize) {
			this.tileW = newTileSize;
			this.tileH = newTileSize;

			// Пересчёт размеров canvas
			const newWidth = this.cols * this.tileW + (this.cols - 1) * this.gap;
			const newHeight = this.rows * this.tileH + (this.rows - 1) * this.gap;

			// 1. Изменяем размер canvas
			this.scale.resize(newWidth, newHeight);

			// 2. Обновляем размер камеры
			this.cameras.main.setSize(newWidth, newHeight);

			// 3. Обновляем bounds для input системы
			this.scale.updateBounds();

			// 4. Пересчёт offset для центрирования
			this.offsetX = Math.floor((this.cameras.main.width - newWidth) / 2);
			this.offsetY = Math.floor((this.cameras.main.height - newHeight) / 2);

			// 5. Обновляем позиции и размеры всех спрайтов
			for (let y = 0; y < this.rows; y++) {
				for (let x = 0; x < this.cols; x++) {
					const spr = this.grid[y][x];
					if (spr) {
						// Получаем оригинальный размер текстуры
						const originalWidth = spr.frame.width;
						const originalHeight = spr.frame.height;

						spr.x = this.getWorldPosX(x);
						spr.y = this.getWorldPosY(y);
						spr.displayWidth = this.tileW;
						spr.displayHeight = this.tileH;
						// Пересоздать interactive с hit area в оригинальных координатах
						spr.removeInteractive();
						spr.setInteractive(
							new this.Phaser.Geom.Rectangle(0, 0, originalWidth, originalHeight),
							this.Phaser.Geom.Rectangle.Contains
						);
						// Переподключить обработчик события
						spr.off('pointerdown');
						spr.on('pointerdown', () => {
							this.onTilePointerDown(spr);
						});
					}
				}
			}
		}
	};
}
